import React, { useEffect, useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import LoadingSpinner from 'components/LoadingSpinner'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import { Button, Card, Container } from 'react-bootstrap'
import FloatingButtonWrapper from 'components/FloatingButtonWrapper'
import PickingRow from 'components/PickingRow'
import PrintingRow from 'components/PrintingRow'
import pickingListRowStatus from 'enums/pickingListRowStatus'
import BarCodeScanner from 'features/BarCodeScanner'
import barcode from '../ArticleSearch/assets/barcodes.svg'
import usePhysicalScanner from 'lib/hooks/usePhysicalScanner'
import ConfirmationWindow from 'components/ConfirmationWindow/ConfirmationWindow'
import usePrinters from 'lib/hooks/usePrinters'
import Printing from 'features/Printing/Printing'
import { toggleShowPrinting } from 'features/Printing/duck'
import SelectRowCorrection from './SelectRowCorrection'
import PickingListCreateForm from 'components/PickingListCreateForm'
import { toggleShowScanner } from 'features/BarCodeScanner/barCodeScannerSlice'
import { showMessage } from '../MessageBox/duck'
import { isAccessory } from 'lib/utils'
import FloatingNumpad from 'components/FloatingNumpad'

import './style.css'

import {
	useBundleItemsMutation,
	useCompletePickingListMutation,
	useLazyFetchPickingFilterQuery,
	useLazyFetchPickingListQuery,
	usePerformPickingListRowCorrectionMutation,
	useStartNewPickingListMutation,
	usePerformPickingListRowDeleteCorrectionMutation,
	usePerformPickingListRowPartialCorrectionMutation,
	usePerformParcelCountUpdateMutation,
	useLazyFetchAwaitingLabellingQuery
} from 'api/pickingApi'

const Picking = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	const showScanner = useSelector((state) => state.barCodeScanner.showScanner)

	// RTK api
	const [triggerStartNewPickingList, { isLoading: isStartNewPickingListLoading }] = useStartNewPickingListMutation()
	const [triggerFetchPickingList, { data: pickingList, isFetching: isFetchPickingListFetching }] = useLazyFetchPickingListQuery()
	const [triggerPerformPickingListRowCorrection, { isLoading: isRowCorrectionLoading }] = usePerformPickingListRowCorrectionMutation()
	const [triggerCompletePickingList, { isLoading: isCompletePickingListLoading }] = useCompletePickingListMutation()
	const [triggerFetchPickingFilter, { data: pickingFilter, isFetching: isFetchPickingFilterFetching }] = useLazyFetchPickingFilterQuery()
	const [triggerBundleItems, { isLoading: isBundleItemsLoading }] = useBundleItemsMutation()
	const [triggerDeleteCorrection, { isLoading: isDeleteCorrectionLoading }] = usePerformPickingListRowDeleteCorrectionMutation()
	const [triggerPartialCorrection, { isLoading: isPartialCorrectionLoading }] = usePerformPickingListRowPartialCorrectionMutation()
	const [triggerPerformParcelCountUpdate, { isLoading: isParcelCountUpdateLoading }] = usePerformParcelCountUpdateMutation()
	const [triggerFetchAwaitingLabelling, { data: awaitingLabelling, isFetching: isAwaitingLabellingLoading }] = useLazyFetchAwaitingLabellingQuery()

	const [updatedPickingList, setUpdatedPickingList] = useState(pickingList)
	const [ordersAwaitingLabelling, setOrdersAwaitingLabelling] = useState(awaitingLabelling)
	const [scannedArticles, setScannedArticles] = useState({})
	const pickingListRows = updatedPickingList?.rows

	const showPrinting = useSelector((state) => state.printing.showPrinting)

	const [selectedBuilding, setSelectedBuilding] = useState('')
	const [selectedFloor, setSelectedFloor] = useState('')
	const [selectedProductGroup, setSelectedProductGroup] = useState('')
	const [selectedAccessoryOption, setSelectedAccessoryOption] = useState('')
	const [selectedPickingType, setSelectedPickingType] = useState('0')

	const [selectedGroup, setSelectedGroup] = useState('')
	const [selectedLocation, setSelectedLocation] = useState(null)
	const [warnOnGoHome, setWarnOnGoHome] = useState(false)
	const [onSelectPrintCount, setOnSelectPrintCount] = useState(false)

	const [noAlternativeLocationError, setNoAlternativeLocationError] = useState(false)

	const [correctionData, setCorrectionData] = useState(null)

	const [labelCount, setLabelCount] = useState('1')
	const [orderToPrint, setOrderToPrint] = useState('')
	const [bundleArticles, setBundleArticles] = useState([])
	const [bundleTarget, setBundleTarget] = useState(null)
	const [parcelCountTarget, setParcelCountTarget] = useState(null)

	const onScannedArticleRef = useRef()

	const hasPickingList = pickingList?.rows?.length > 0
	const pickingListContainAccessory = pickingList?.rows?.some((item) => isAccessory(item.mainGroupId))
	const hasAwaitingLabelling = ordersAwaitingLabelling?.length > 0

	const { printPickingList, printPalletLabel } = usePrinters()

	const goHome = () => {
		setWarnOnGoHome(false)
		setOnSelectPrintCount(false)
		setScannedArticles({})
		setLabelCount('1')
		navigate('/')
	}

	const handleGoHome = () => {
		if (hasAwaitingLabelling) {
			goHome()
		} else {
			setWarnOnGoHome(true)
		}
	}

	const startNewList = async () => {
		const formattedPayload = startNewListPayload()

		await triggerStartNewPickingList(formattedPayload)
		fetchAndSetPickingList()
	}

	const fetchAndSetOrdersAwaitingLabeling = () => async () => {
		const payload = { pickingGroup: Number(selectedGroup) }
		const result = await triggerFetchAwaitingLabelling(payload)
		setOrdersAwaitingLabelling(result.data)
		setLabelCount('1')
		setOrderToPrint('')
	}

	const startNewListPayload = () => {
		return {
			pickingGroupId: Number(selectedGroup),
			nextBusinessDay: false,
			pickingBuilding: Number(selectedBuilding),
			pickingFloorOption: Number(selectedFloor),
			pickingProductGroup: Number(selectedProductGroup),
			excludeAccessories: Number(selectedAccessoryOption),
			isPalletPicking: isPalletPicking,
		}
	}

	const handleOnChangeParcelAmount = (value, isBundling) => {
		if (isBundling)
			setBundleTarget(prevBundleTarget => ({
				...prevBundleTarget,
				parcelAmount: value,
			}))
		else {
			try {
				// Find the row matching the first pickingRowId
				const rowId = value.pickingRowIds?.[0]?.rowId
				const row = pickingList.rows?.find(r => r.rowId === rowId)
				const stockLocation = row?.stockLocation

				const formattedPayload = {
					sequenceNumber: pickingList.sequenceNumber,
					parcelCount: value.parcelCount,
					stockLocation: stockLocation,
					pickingRowIds: value.pickingRowIds
				}
				setParcelCountTarget({
					...formattedPayload,
				})
				sendParcelCountUpdate(formattedPayload)
			} catch (error) {
				console.error('Error updating parcel amount:', error)
			}
		}
	}

	const sendParcelCountUpdate = async (payload) => {
		try {
			await triggerPerformParcelCountUpdate(payload)
			dispatch(showMessage('Parcel count updated!'))
		} catch (error) {
			dispatch(showMessage('Failed to update parcel count.'))
		}
	}

	const onHandleSelectedBundleTargetOrder = (selectedOrderId) => {
		setBundleTarget({ ...bundleTarget, selectedOrderId: selectedOrderId })
	}

	const onHandleBundle = (rowIndex, articleId, stockLocation, rowIds, orderIds, parcelAmount, selectedOrderId) => {

		const newItem = { rowIndex, articleId, stockLocation, rowIds, orderIds, parcelAmount, selectedOrderId }

		if (!bundleTarget) {
			setBundleTarget(newItem)
			return
		}

		if (bundleTarget.rowIndex === rowIndex && bundleTarget.articleId === articleId && bundleTarget.stockLocation === stockLocation) {
			setBundleTarget(null)
			setBundleArticles([])
			return
		}

		// Check if the item already exists in bundleArticles
		const itemExists = bundleArticles?.some(
			(item) => item.rowIndex === rowIndex && item.articleId === articleId && item.stockLocation === stockLocation
		)

		if (!itemExists) {
			setBundleArticles((prevBundleArticles) => [...(prevBundleArticles || []), newItem])
		}
		else if (itemExists) {
			setBundleArticles((prevBundleArticles) => prevBundleArticles.filter(
				(item) => !(item.rowIndex === rowIndex && item.articleId === articleId && item.stockLocation === stockLocation && item.rowIds === rowIds && item.orderIds === orderIds)
			))
		}
	}

	const onChangePickingBuilding = (e) => {
		setSelectedBuilding(e.target.value)
	}

	const onChangePickingFloor = (e) => {
		setSelectedFloor(e.target.value)
	}

	const onChangePickingProductGroup = (e) => {
		setSelectedProductGroup(e.target.value)
	}

	const onChangePickingAccessory = (e) => {
		setSelectedAccessoryOption(e.target.value)
	}

	const onChangePickingType = (e) => {
		setSelectedPickingType(e.target.value)
	}

	const onChangePickingGroup = (e) => {
		setSelectedGroup(e.target.value)
	}

	const canCreatePickingList = () => {
		return selectedGroup && selectedPickingType !== '0'
	}

	const handlePicked = async (value, rowIndex, partiallyPickedQuantity) => {
		const row = updatedPickingList?.rows?.[rowIndex]

		if (value === 2 && row) {
			const { articleId, ean, stockLocation } = row

			const correctionData = {
				ArticleId: articleId,
				StockLocation: stockLocation,
				PickedQuantity: scannedArticles[stockLocation]?.[ean] || partiallyPickedQuantity,
				PickingGroupId: Number(selectedGroup),
				NextBusinessDay: false,
			}

			setCorrectionData(correctionData)

			const correctionResult = await triggerPerformPickingListRowCorrection(correctionData)
			if (correctionResult?.error?.status === 409) {
				setNoAlternativeLocationError(true)
			}

			fetchAndSetPickingList()
		} else if (value === 0 && row) {
			const { ean, stockLocation } = row

			setScannedArticles({
				...scannedArticles,
				[stockLocation]: { ...scannedArticles[stockLocation], [ean]: 0 }
			})
			updatePickingStatus({ rowIndex, pickingStatus: value })
		} else {
			updatePickingStatus({ rowIndex, pickingStatus: value })
		}
	}

	const updatePickingStatus = ({ rowIndex, pickingStatus }) => {
		setUpdatedPickingList((prevPickingList) => {
			const updatedRows = prevPickingList.rows.map((row, index) => {
				if (rowIndex === index) {
					return { ...row, pickingStatus }
				}
				return row
			})
			return { ...prevPickingList, rows: updatedRows }
		})
	}

	const savePicking = async () => {

		try {
			await triggerCompletePickingList(pickingList)
			await triggerFetchPickingFilter()
			dispatch(showMessage('Picking list completed!'))
			navigate(-1)
		} catch (error) {
			dispatch(showMessage('An error occurred while saving the picking list.'))
			console.error('Error in savePicking:', error)
		}
	}

	const onNumpadChange = (value) => {
		const newValue = value === '' ? '' : Math.max(1, Math.min(99, Number.parseInt(value))).toString()
		setLabelCount(newValue)
	}

	const onConfirmNumpad = () => {
		togglePrint()
	}

	const onClearNumpad = () => {
		setLabelCount('')
	}

	const updateScannedArticles = useCallback(
		(location, ean) => {
			const matchingItem = pickingListRows.find(
				(item) =>
					String(item.ean) === String(ean) &&
					String(item.stockLocation) === String(location) &&
					String(item.pickingStatus) === String(pickingListRowStatus.None),
			)

			// Calculate the new scanned quantity for the scanned article
			const newScannedQuantity =
				(scannedArticles[location] && scannedArticles[location][ean]) || 0

			// Check if the new scanned quantity doesn't exceed the picking row's quantity
			if (newScannedQuantity < matchingItem.quantity) {
				const updatedRows = pickingListRows.map((item) => {
					if (item.ean === ean && item.stockLocation === location) {
						return { ...item, quantity: item.quantity + 1 }
					}
					return item
				})
				updatePickingStatus(updatedRows)
				const updatedScannedItem = {
					...scannedArticles[location],
					[ean]: newScannedQuantity + 1,
				}
				// Update scannedArticles for the specific location
				setScannedArticles({
					...scannedArticles,
					[location]: updatedScannedItem,
				},
				)
			} else {
				dispatch(
					showMessage('Scanned quantity exceeds the picking rows quantity.'),
				)
			}

			if ((newScannedQuantity + 1) === matchingItem.quantity) {
				setSelectedLocation(null)
			}

		},
		[dispatch, scannedArticles, pickingListRows, setSelectedLocation],
	)

	const onScannedArticle = useCallback(
		(value) => {

			const matchingLocation = pickingListRows.find(
				(item) => item.ean === parseFloat(value),
			)

			if (matchingLocation) {
				setSelectedLocation(matchingLocation.stockLocation)
				updateScannedArticles(matchingLocation.stockLocation, value)
			} else {
				dispatch(
					showMessage(`Location ${value} not found in the picking list.`),
				)
			}
		},
		[pickingListRows, dispatch, setSelectedLocation, updateScannedArticles],
	)

	const calculatePickedValue = (stockLocation) => {
		if (scannedArticles[stockLocation]) {
			const quantities = Object.values(scannedArticles[stockLocation])
			const totalPicked = quantities.reduce(
				(total, quantity) => total + quantity,
				0,
			)

			// Get the total quantity allowed for this location
			const totalQuantity =
				pickingList.rows.find((item) => item.stockLocation === stockLocation)
					?.quantity || 0

			// Ensure that the total picked quantity does not exceed the total quantity allowed
			return Math.min(totalPicked, totalQuantity)
		}

		return 0
	}

	const performBundling = () => {
		triggerBundleItems({ sequenceNumber: pickingList.sequenceNumber, target: bundleTarget, rows: bundleArticles })
		setBundleArticles([])
		setBundleTarget(null)
	}

	const removeRowFromList = async () => {
		setNoAlternativeLocationError(false)
		await triggerDeleteCorrection(correctionData)
		fetchAndSetPickingList()
	}

	const removeAmountFromList = async () => {
		setNoAlternativeLocationError(false)
		await triggerPartialCorrection(correctionData)
		fetchAndSetPickingList()
	}

	const selectPrint = () => {
		if (isPalletPicking) {
			setOnSelectPrintCount(true)
		} else {
			togglePrint()
		}
	}

	const togglePrintSingleOrder = (orderId, labelCount) => {
		setLabelCount(labelCount)
		setOrderToPrint(orderId)
		togglePrint()
	}

	const togglePrint = () => {
		setOnSelectPrintCount(false)

		// Show reminder if picking list contains accessories
		if (checkIfAccessories()) {
			dispatch(showMessage('Reminder', 'Pickinglist contains accessories, any bundling or parcel quantity changes should be performed before printing!'))
		}

		dispatch(toggleShowPrinting())
	}

	const checkIfAccessories = () => {
		return pickingList?.rows?.some((item) => isAccessory(item.mainGroupId))
	}

	const onPrintPicking = (selectedPrinter) => {
		if (isPalletPicking) {
			const orders = pickingList.rows.map(row => row.orderIds.map(order => order.orderId)).flat(2)
			printPalletLabel(selectedPrinter, orders, Number(labelCount))
		} else if (isAwatingLabelling) {
			let orders
			if (orderToPrint) {
				orders = [orderToPrint]
			} else {
				orders = ordersAwaitingLabelling.map(x => x.orderId)
			}
			printPalletLabel(selectedPrinter, orders, Number(labelCount))

			// After printing, remove the printed orders from the list. The order takes a few seconds to update its status in the backend, so we optimistically remove it from the UI.
			setOrdersAwaitingLabelling(ordersAwaitingLabelling.filter(x => !orders.includes(x.orderId)))

		} else {
			printPickingList(selectedPrinter, pickingList.sequenceNumber)
		}

		setLabelCount('1')
		setOrderToPrint('')
	}

	const toggleScanner = () => {
		dispatch(toggleShowScanner())
	}

	const onCloseLocationError = () => {
		setNoAlternativeLocationError(false)
	}

	const filterList = (bundleTarget, pickingList) => {
		if (!bundleTarget || !pickingList?.rows) {
			return pickingList?.rows || []
		}

		const updatedList = pickingList.rows
			.map((item, index) => ({
				...item,
				originalRowIndex: index,
			}))
			.filter((item) => {
				// Check if there is any overlapping OrderId between item and bundleTarget
				const hasCommonOrderId =
					item.orderIds?.some((orderIdPair) =>
						bundleTarget?.orderIds?.some(
							(bundleOrderIdPair) => bundleOrderIdPair.orderId === orderIdPair.orderId
						)
					) && !item.bundle

				// Check if the item matches the selectedOrderId on bundleTarget if it exists
				const matchesSelectedOrderId =
					bundleTarget?.selectedOrderId &&
					item.orderIds?.some(
						(orderIdPair) => orderIdPair.orderId === Number(bundleTarget?.selectedOrderId)
					)

				return bundleTarget.selectedOrderId ? matchesSelectedOrderId : hasCommonOrderId
			})

		return updatedList
	}

	useEffect(() => {
		onScannedArticleRef.current = onScannedArticle
	}, [onScannedArticle])

	usePhysicalScanner((barcode) => {
		onScannedArticleRef.current(barcode)
	}, [])

	const fetchAndSetPickingList = async () => {
		try {
			const result = await triggerFetchPickingList({ nextBusinessDay: false }).unwrap()
			setUpdatedPickingList(result)
		} catch (error) {
			dispatch(showMessage('An error occurred while fetching the picking list.'))
		}
	}

	useEffect(() => {
		triggerFetchPickingFilter()
		fetchAndSetPickingList()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const showCompletePickingList = !updatedPickingList?.rows?.some((i) => i.pickingStatus === pickingListRowStatus.None)

	const inBundling = bundleTarget && bundleArticles?.length > 0

	const listToMap = filterList(bundleTarget, updatedPickingList)

	const loading =
		isStartNewPickingListLoading ||
		isFetchPickingListFetching ||
		isRowCorrectionLoading ||
		isCompletePickingListLoading ||
		isFetchPickingFilterFetching ||
		isBundleItemsLoading ||
		isDeleteCorrectionLoading ||
		isPartialCorrectionLoading ||
		isParcelCountUpdateLoading ||
		isAwaitingLabellingLoading

	const loadingSpinner = loading ? <LoadingSpinner /> : ''

	const isPalletPicking = pickingList?.isPalletPicking || selectedPickingType === '2'
	const isAwatingLabelling = selectedPickingType === '3'


	return (
		<>
			<Container className="picking">
				{loadingSpinner}
				<div className="justify-content-center">
					{showPrinting && <Printing onPrint={onPrintPicking} />}

					<Col xs={12} sm={12}>
						<Row className="justify-content-center">
							{showScanner && (
								<BarCodeScanner
									onScannedResult={onScannedArticle}
									isPicking={true}
									scannedArticles={scannedArticles}
								/>
							)}
							{pickingList && (
								<div className="pickingListInformation">
									{isPalletPicking && <div>Picking group: {updatedPickingList?.pickingGroupDisplayText} (Pallet)</div>}
									{!isPalletPicking && <div>Picking group: {updatedPickingList?.pickingGroupDisplayText}</div>}
									<div>Sequence number: {updatedPickingList?.sequenceNumber}</div>
								</div>
							)}

							{hasPickingList &&
								listToMap?.map((item, index) => (
									<Card key={index} className="row-card">
										<PickingRow
											selected={item.stockLocation === selectedLocation}
											pickedValue={calculatePickedValue(item.stockLocation)}
											totalValue={item.quantity}
											item={item}
											handlePicked={handlePicked}
											rowIndex={item.originalRowIndex || index}
											onBundle={onHandleBundle}
											disabled={bundleTarget}
											bundleTarget={bundleTarget}
											parcelCountTarget={parcelCountTarget}
											pickingListContainAccessory={pickingListContainAccessory}
											onChangeParcelAmount={handleOnChangeParcelAmount}
											selectOrderOnBundleTarget={onHandleSelectedBundleTargetOrder}
											selectedBundleTargetOrderId={bundleTarget?.selectedOrderId}
											isBundleTarget={
												(item.originalRowIndex === bundleTarget?.rowIndex) &&
												item.articleId === bundleTarget?.articleId
												&& item.stockLocation === bundleTarget?.stockLocation
											}
											toBeBundled={bundleArticles.some(
												(bundleItem) =>
													bundleItem.rowIndex === item.originalRowIndex &&
													bundleItem.articleId === item.articleId &&
													bundleItem.stockLocation === item.stockLocation
											)}
										/>
									</Card>
								))}

							{hasAwaitingLabelling &&
								ordersAwaitingLabelling?.map(x => (
									<Card key={x.orderId} className="row-card" >
										<PrintingRow
											orderId={x.orderId}
											customerName={x.customerName}
											onPrintLabels={togglePrintSingleOrder}
										/>

									</Card>
								))}
						</Row>

						{!hasPickingList && !hasAwaitingLabelling && !loading && (
							<PickingListCreateForm
								pickingGroups={pickingFilter?.pickingGroupList}
								pickingBuildings={pickingFilter?.buildingList}
								selectedGroup={selectedGroup}
								selectedBuilding={selectedBuilding}
								selectedFloor={selectedFloor}
								selectedProductGroup={selectedProductGroup}
								selectedAccessoryOption={selectedAccessoryOption}
								selectedPickingType={selectedPickingType}
								onChangePickingGroup={onChangePickingGroup}
								onChangePickingBuilding={onChangePickingBuilding}
								onChangePickingFloor={onChangePickingFloor}
								onChangePickingProductGroup={onChangePickingProductGroup}
								onChangePickingAccessory={onChangePickingAccessory}
								onPickingTypeChange={onChangePickingType}
							/>
						)}

					</Col>
				</div>
				{warnOnGoHome && (
					<ConfirmationWindow
						title="Leave?"
						message="Pickinglist is still active, leave anyway?"
						confirmLabel={'Yes'}
						cancelLabel={'No'}
						onCancel={() => setWarnOnGoHome(false)}
						onConfirm={goHome}
					/>
				)}
				{onSelectPrintCount && (
					<FloatingNumpad
						headerText={'Enter number of labels to print'}
						closeOutSide={true}
						text={String(labelCount)}
						onChange={onNumpadChange}
						onSend={onConfirmNumpad}
						hideExtraButton={true}
						onClear={onClearNumpad}
						showStockButton={false}
					/>

				)}
				{ }
			</Container>
			<FloatingButtonWrapper>
				{
					inBundling &&
					<Button variant="warning" onClick={performBundling} size="lg" disabled={!bundleArticles?.length >= 2}>
						Bundle selected
					</Button>
				}
				{(hasPickingList || hasAwaitingLabelling) && !inBundling && (
					<Button variant="info" onClick={selectPrint} size="lg" disabled={bundleTarget}>
						Print labels
					</Button>
				)}
				{!hasPickingList && !inBundling && !loading && !isAwatingLabelling && (
					<Button
						variant="success"
						disabled={!canCreatePickingList()}
						onClick={startNewList}
						size="lg"
					>
						Start new pickinglist
					</Button>
				)}
				{!hasPickingList && !inBundling && !loading && !hasAwaitingLabelling && isAwatingLabelling && (
					<Button
						variant="success"
						onClick={fetchAndSetOrdersAwaitingLabeling()}
						size="lg"
					>
						View Orders Awaiting Labelling
					</Button>
				)}
				{hasPickingList && !inBundling && showCompletePickingList && (
					<Button variant="success" onClick={savePicking} size="lg">
						Complete picking list
					</Button>
				)}
				<Button
					className="btnScan"
					variant="secondary"
					type="button"
					id={'switchInputType'}
					onClick={toggleScanner}
				>
					<img src={barcode} alt="barcode input"></img>
				</Button>
				<Button variant="danger" size="lg" onClick={handleGoHome}>
					Home
				</Button>
			</FloatingButtonWrapper>
			{
				noAlternativeLocationError &&
				<SelectRowCorrection
					title="Correction alert"
					disableRemoveAmount={correctionData.PickedQuantity === '0'}
					onClickRemoveAmount={removeAmountFromList}
					onClickRemoveRow={removeRowFromList}
					onClose={onCloseLocationError}
				/>
			}
		</>
	)
}

export default Picking
